// hooks/useAlert.ts
import { useDispatch } from 'react-redux'
import { useCallback } from 'react'
import { NotificationType } from '../utils/client-constants'
import { hideAlert, showAlert } from '../redux/features/notification/alert-slice'
import {
  AlertBoxState,
  AlertOptions,
  apiErrorAlert,
  basicErrorAlertProps,
  basicInfoAlertProps
} from '../redux/features/notification/modal-state'
import { ApiError } from 'app/services/common-error-utils'

export const useAlert = () => {
  const dispatch = useDispatch()

  const notify = useCallback(
    (type: NotificationType, alertState: Omit<AlertBoxState, 'type'>) => {
      const { isLoadingPrimaryButton, isWaitForAction, ...options } = alertState

      const onPrimaryClickHandler = () => {
        if (!options.onPrimaryClick) {
          return undefined
        }
        options.onPrimaryClick()
        if (!isLoadingPrimaryButton && !isWaitForAction) {
          dispatch(hideAlert())
        }
      }

      const onSecondaryClickHandler = () => {
        if (!options.onSecondaryClick) {
          return undefined
        }
        options.onSecondaryClick()
        dispatch(hideAlert())
      }

      dispatch(
        showAlert({
          ...options,
          type,
          isLoadingPrimaryButton: isLoadingPrimaryButton || false,
          onPrimaryClick: options.onPrimaryClick ? onPrimaryClickHandler : undefined,
          onSecondaryClick: options.onSecondaryClick ? onSecondaryClickHandler : undefined
        })
      )
    },
    [dispatch]
  )

  return {
    error: (description: string, options?: AlertOptions) =>
      notify(NotificationType.ERROR, basicErrorAlertProps(getAlertOptions(description, options))),
    apiError: (error: ApiError, onPrimary?: () => void) =>
      notify(NotificationType.ERROR, apiErrorAlert(undefined, error, onPrimary)),
    warning: (alertState: AlertBoxState) => notify(NotificationType.WARNING, alertState),
    info: (description: string, options?: AlertOptions) =>
      notify(NotificationType.INFO, basicInfoAlertProps(getAlertOptions(description, options))),
    custom: (alertState: AlertBoxState) => notify(alertState.type as NotificationType, alertState),
    hide: () => dispatch(hideAlert())
  }
}

const getAlertOptions = (description: string, options?: AlertOptions) => {
  const { title, onPrimary, onSecondary, loadingPrimary } = options || {}
  return {
    description,
    title,
    onPrimary,
    onSecondary,
    loadingPrimary
  }
}
